import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { BookMarked, Plus } from "lucide-react"
import { useRef, useState } from "react"
import { Link } from "react-router"

import { api } from "@/api/cliente"
import { Exportar } from "@/componentes/exportar/Exportar"
import { monto } from "@/componentes/tesoreria/dinero"
import { Boton } from "@/componentes/ui/Boton"
import { EstadoCarga, EstadoError } from "@/componentes/ui/Estados"
import { Insignia } from "@/componentes/ui/Insignia"
import { Cabecera, Cuerpo, Fila, Tabla, TablaVacia, Td, Th } from "@/componentes/ui/Tabla"
import { tienePermiso } from "@/lib/autorizacion"
import { usarSesion } from "@/lib/sesion"
import type { CategoriaGasto, CuentaContable } from "@/paginas/interno/Gastos"

type PorCuenta = { cuenta_codigo?: string | null; ejecutado: string; por_cancelar: string }
type Borrador = { codigo: string; descripcion: string; tipo: string; activo: boolean }
const VACIO: Borrador = { codigo: "", descripcion: "", tipo: "GASTO", activo: true }

/**
 * Contabilidad → Plan de cuentas (28/10). Las partidas traídas del 2.0 tenían datos pero no
 * pantalla: sólo se veían en el selector de cada categoría. Aquí, cada partida con las
 * categorías de gasto que cuelgan de ella y lo ejecutado del mes en curso.
 *
 * ⚠ **Una partida no se borra: se archiva.** Sus categorías y su histórico siguen colgando
 * de ella; archivada, deja de ofrecerse al elegir la cuenta de una categoría.
 */
export function PlanCuentas() {
  const usuario = usarSesion((e) => e.usuario)
  const puede = tienePermiso(usuario, "gasto:administrar")
  const veInforme = tienePermiso(usuario, "informe_gasto:ver")
  const clienteQuery = useQueryClient()
  const [editando, setEditando] = useState<number | "nueva" | null>(null)
  const [b, setB] = useState<Borrador>(VACIO)
  const vista = useRef<HTMLDivElement | null>(null)

  const plan = useQuery({
    queryKey: ["plan-cuentas"],
    queryFn: () => api.get<CuentaContable[]>("/api/v1/plan-cuentas"),
  })
  const categorias = useQuery({
    queryKey: ["categorias-gasto"],
    queryFn: () => api.get<CategoriaGasto[]>("/api/v1/categorias-gasto"),
  })
  const mes = useQuery({
    queryKey: ["cuentas-y-resultados", "plan-mes"],
    queryFn: () =>
      api.get<{ por_cuenta?: PorCuenta[] }>("/api/v1/informes/cuentas-y-resultados?periodo=MES"),
    enabled: veInforme,
  })
  const guardar = useMutation({
    mutationFn: () =>
      editando === "nueva"
        ? api.post("/api/v1/plan-cuentas", b)
        : api.put(`/api/v1/plan-cuentas/${editando}`, b),
    onSuccess: () => {
      clienteQuery.invalidateQueries({ queryKey: ["plan-cuentas"] })
      setEditando(null)
    },
  })
  const archivar = useMutation({
    mutationFn: (c: CuentaContable) =>
      api.put(`/api/v1/plan-cuentas/${c.id}`, {
        codigo: c.codigo,
        descripcion: c.descripcion,
        tipo: c.tipo,
        activo: !c.activo,
      }),
    onSuccess: () => clienteQuery.invalidateQueries({ queryKey: ["plan-cuentas"] }),
  })

  if (plan.isPending) return <EstadoCarga etiqueta="Cargando el plan de cuentas" />
  if (plan.isError) {
    return (
      <EstadoError
        titulo="No se pudo cargar"
        error={plan.error}
        onReintentar={() => plan.refetch()}
      />
    )
  }
  const ejecutado = new Map((mes.data?.por_cuenta ?? []).map((f) => [f.cuenta_codigo ?? "", f]))
  const deCuenta = (id: number) => (categorias.data ?? []).filter((c) => c.cuenta_id === id)
  const sinCuenta = (categorias.data ?? []).filter((c) => c.activo && !c.cuenta_id)
  const error = guardar.error ?? archivar.error

  const formulario = (
    <form
      className="tarjeta grid gap-3 p-4 sm:grid-cols-[8rem_1fr_9rem_auto] sm:items-end"
      onSubmit={(e) => {
        e.preventDefault()
        guardar.mutate()
      }}
    >
      <label className="text-sm">
        Código
        <input
          className="campo mt-1"
          required
          placeholder="3.3"
          value={b.codigo}
          onChange={(e) => setB({ ...b, codigo: e.target.value })}
        />
      </label>
      <label className="text-sm">
        Descripción
        <input
          className="campo mt-1"
          required
          minLength={2}
          placeholder="Seguros"
          value={b.descripcion}
          onChange={(e) => setB({ ...b, descripcion: e.target.value })}
        />
      </label>
      <label className="text-sm">
        Tipo
        <select
          className="campo mt-1"
          value={b.tipo}
          onChange={(e) => setB({ ...b, tipo: e.target.value })}
        >
          <option value="GASTO">Gasto</option>
          <option value="INGRESO">Ingreso</option>
        </select>
      </label>
      <div className="flex gap-2">
        <Boton type="submit" variante="primario" disabled={guardar.isPending}>
          Guardar
        </Boton>
        <Boton onClick={() => setEditando(null)}>Cancelar</Boton>
      </div>
    </form>
  )

  return (
    <div ref={vista} className="mx-auto max-w-5xl space-y-4">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-xl font-semibold">
            <BookMarked size={20} aria-hidden={true} /> Plan de cuentas
          </h1>
          <p className="mt-1 text-sm" style={{ color: "var(--fg-muted)" }}>
            Las partidas contables, las categorías de gasto que cuelgan de cada una y lo ejecutado
            este mes. La cuenta de una categoría se cambia en Administración → Gastos y categorías.
          </p>
        </div>
        <div className="flex flex-wrap items-start gap-2">
          <Exportar
            titulo="Plan de cuentas"
            subtitulo={veInforme ? "Partidas, categorías y ejecutado del mes en curso" : undefined}
            contenido={vista}
          />
          {puede && editando === null && (
            <Boton
              variante="primario"
              onClick={() => {
                setB(VACIO)
                setEditando("nueva")
              }}
            >
              <Plus size={16} /> Nueva partida
            </Boton>
          )}
        </div>
      </header>

      {error && (
        <p role="alert" className="tarjeta p-3 text-sm" style={{ color: "var(--destructive)" }}>
          {(error as Error).message}
        </p>
      )}
      {editando !== null && formulario}

      <Tabla etiqueta="Plan de cuentas">
        <Cabecera>
          <Th>Partida</Th>
          <Th>Categorías de gasto</Th>
          {veInforme && <Th className="text-right">Ejecutado del mes</Th>}
          <Th>
            <span className="sr-only">Acciones</span>
          </Th>
        </Cabecera>
        <Cuerpo>
          {plan.data.length === 0 && <TablaVacia colSpan={4}>No hay partidas.</TablaVacia>}
          {plan.data.map((c) => {
            const cats = deCuenta(c.id)
            const e = ejecutado.get(c.codigo)
            return (
              <Fila key={c.id}>
                <Td>
                  <span className="font-medium tabular-nums">{c.codigo}</span> {c.descripcion}
                  <span className="ml-2 inline-flex gap-1">
                    {c.tipo === "INGRESO" && <Insignia tono="info">Ingreso</Insignia>}
                    {!c.activo && <Insignia tono="neutro">Archivada</Insignia>}
                  </span>
                </Td>
                <Td className="text-xs">
                  {cats.length === 0 ? (
                    <span style={{ color: "var(--fg-muted)" }}>—</span>
                  ) : (
                    cats.map((k) => k.nombre).join(" · ")
                  )}
                </Td>
                {veInforme && (
                  <Td className="text-right tabular-nums">
                    {e ? `${monto(e.ejecutado)} $` : "—"}
                    {e && Number(e.por_cancelar) > 0 && (
                      <span className="block text-xs" style={{ color: "var(--fg-muted)" }}>
                        + {monto(e.por_cancelar)} $ por cancelar
                      </span>
                    )}
                  </Td>
                )}
                <Td>
                  {puede && (
                    <div className="flex gap-2">
                      <Boton
                        tamano="sm"
                        onClick={() => {
                          setB({
                            codigo: c.codigo,
                            descripcion: c.descripcion,
                            tipo: c.tipo,
                            activo: c.activo,
                          })
                          setEditando(c.id)
                        }}
                      >
                        Editar
                      </Boton>
                      <Boton tamano="sm" onClick={() => archivar.mutate(c)}>
                        {c.activo ? "Archivar" : "Reactivar"}
                      </Boton>
                    </div>
                  )}
                </Td>
              </Fila>
            )
          })}
        </Cuerpo>
      </Tabla>

      {sinCuenta.length > 0 && (
        <p className="tarjeta p-3 text-sm" style={{ color: "var(--warning)" }}>
          Categorías sin partida: {sinCuenta.map((k) => k.nombre).join(", ")}.{" "}
          <Link to="/administracion/gastos" className="underline">
            Asígnalas aquí
          </Link>
          .
        </p>
      )}
    </div>
  )
}
